import React, { FunctionComponent } from 'react'
import classnames from 'classnames'
import { NavLink } from 'react-router-dom'

import { utcToLocalDateTimeFormat } from '../../../helpers/timeHelper'
import { convertDecimalToPercent } from '../../../helpers/displayFormatHelper'
import { StatusPill } from '../../common/statusPill'
import { Address } from '../../../core/types'

export type Callback<T = void> = (...args: any) => T

export interface Props {
  address: Address
  badge?: string
  isSelected?: boolean
  noHover?: boolean
  selectAddressCard?: Callback
  status?: string
  userId?: string
}

export const UserAddressCard: FunctionComponent<Props> = ({
  address,
  badge,
  isSelected,
  noHover,
  selectAddressCard,
  status,
  userId,
}) => {
  const badgeLabel = (
    <div className="user-address__badge">
      <p className="user-address__badge--text">{badge}</p>
    </div>
  )

  if (!address)
    return (
      <div
        className={classnames('user-address__card', {
          'user-address__card--no-hover': noHover,
        })}
      >
        <div
          className={classnames('user-address__title', {
            'user-address__title--no-status': !status && !badge,
            'user-address__title--active': isSelected,
          })}
        >
          {badge && badgeLabel}
          {status && <StatusPill withIcon status={status} />}
        </div>

        <div className="user-address__card--content">
          <p className="user-address__card--empty">No address entered</p>
        </div>
      </div>
    )

  const {
    id,
    city,
    county,
    createdAt,
    line1,
    line2,
    state,
    vehicleTaxRate,
    zip,
  } = address

  return (
    <div
      className={classnames('user-address__card', {
        'user-address__card--no-hover': noHover,
      })}
      onClick={() => selectAddressCard && selectAddressCard(id)}
      data-testid="useraddress-click"
    >
      <div
        className={classnames('user-address__title', {
          'user-address__title--no-status': !status && !badge,
          'user-address__title--active': isSelected,
        })}
      >
        {badge && badgeLabel}
        {status && <StatusPill withIcon status={status} />}
        {isSelected && (
          <NavLink
            className="user-address__title--link"
            to={`/users/${userId}/address/${id}/edit`}
          >
            Edit
          </NavLink>
        )}
      </div>

      <div className="user-address__card--content">
        <div className="user-address__card--mailing">
          <p className="user-address__card--main">{line1}</p>
          {line2 && <p className="user-address__card--main">{line2}</p>}
          <p className="user-address__card--main">
            {city}, {state} {zip}
          </p>
        </div>
        <div className="user-address__card--middle">
          <p className="user-address__card--county">{county} County</p>
          <p className="user-address__card--tax">
            {convertDecimalToPercent(vehicleTaxRate)} Tax Rate
          </p>
        </div>
        <p className="user-address__card--date">
          {utcToLocalDateTimeFormat(createdAt)}
        </p>
      </div>
    </div>
  )
}

UserAddressCard.displayName = 'UserAddressCard'
